import orderBy from "lodash/orderBy";
import { WithApiRequestMeta } from "../../api-results";
import { RtkEmptySplitApi } from "../../types";
import { FileType, Directory, DirectoryList, PermissionsMode } from "./types";
import { DriveApiTags } from "./tags";

export const addDriveApi = (emptySplitApi: RtkEmptySplitApi) => {
  const apiWithTags = emptySplitApi.enhanceEndpoints({
    addTagTypes: [DriveApiTags.driveDirectoryContents],
  });
  const driveAPI = apiWithTags.injectEndpoints({
    overrideExisting: false,
    endpoints: (build) => ({
      downloadFileChunk: build.mutation<
        string,
        WithApiRequestMeta<{
          entityId: string;
          id: string;
          range: number[];
        }>
      >({
        query: ({ entityId, meta: _meta, ...params }) => ({
          url: `/api/auth/drive/entity/${entityId}/download`,
          method: "GET",
          params,
        }),
      }),
      downloadGuestFileChunk: build.mutation<
        string,
        WithApiRequestMeta<{
          entityId: string;
          id: string;
          range: number[];
        }>
      >({
        query: ({ entityId, meta: _meta, ...params }) => ({
          url: `/api/auth/drive/entity/${entityId}/guest/download`,
          method: "GET",
          params,
        }),
      }),
      downloadAttachmentChunk: build.mutation<
        string,
        WithApiRequestMeta<{
          params: { range: [number, number]; path: string };
        }>
      >({
        query: ({ params }) => ({
          url: `/api/auth/attachment/download`,
          params,
        }),
      }),
      getDirectoryContents: build.query<
        DirectoryList,
        WithApiRequestMeta<{
          entityId: string;
          parent_id?: string;
        }>
      >({
        query: ({ entityId, parent_id }) => {
          const params: { parent_id?: string; parent_path?: string } = {};
          if (parent_id) {
            if (parent_id.includes(".")) {
              params.parent_path = parent_id;
            } else {
              params.parent_id = parent_id;
            }
          }
          return {
            url: `/api/auth/drive/entity/${entityId}/list`,
            params,
          };
        },
        transformResponse: (response: DirectoryList) => {
          response.data = orderBy(
            orderBy(
              response.data.filter((item) => item.status === "valid"),
              (item) => item.name?.toLocaleLowerCase()
            ),
            (item) => (item.type === FileType.dir ? -1 : 1)
          );
          return response;
        },
        providesTags: (e1, e2, { parent_id: parentDirectory }) => [
          DriveApiTags.driveDirectoryContents,
          {
            type: DriveApiTags.driveDirectoryContents,
            id: parentDirectory,
          },
        ],
      }),
      createDirectory: build.mutation<
        Directory,
        WithApiRequestMeta<{
          entity_id: string;
          data: {
            name: string;
            parent_dir_id?: string;
            workspace_id?: string;
            mode: keyof typeof PermissionsMode;
            users?: string[];
          };
        }>
      >({
        query: ({ data, entity_id }) => ({
          url: `/api/auth/drive/entity/${entity_id}/directory`,
          method: "POST",
          data,
        }),
        invalidatesTags: (
          e1,
          e2,
          { data: { parent_dir_id: parentDirectory } }
        ) => [
          {
            type: DriveApiTags.driveDirectoryContents,
            id: parentDirectory,
          },
          {
            type: DriveApiTags.driveDirectoryContents,
          },
        ],
      }),
      editDirectory: build.mutation<
        {
          entity_id: string;
          status: "valid" | "invalid";
          type: "dir";
          updated_at: string;
          workspade_id: string;
          id: string;
          name: string;
        },
        WithApiRequestMeta<{
          entity_id: string;
          id: string;
          mode: keyof typeof PermissionsMode;
          name: string;
          users: string[];
          parent_dir_id: string;
          workspace_id?: string;
        }>
      >({
        query: ({
          entity_id,
          parent_dir_id,
          workspace_id,
          meta: _meta,
          ...data
        }) => ({
          url: `/api/auth/drive/entity/${entity_id}/edit`,
          method: "PUT",
          data,
        }),
        invalidatesTags: [DriveApiTags.driveDirectoryContents],
      }),
      renameFile: build.mutation<
        {
          entity_id: string;
          id: string;
          name: string;
          status: "valid" | "invalid";
          type: FileType;
          updated_at: string;
          workspade_id?: string;
        },
        WithApiRequestMeta<{
          entity_id: string;
          id: string;
          name: string;
          parent_dir_id?: string;
          workspace_id?: string;
        }>
      >({
        query: ({
          entity_id,
          parent_dir_id,
          workspace_id,
          meta: _meta,
          ...data
        }) => ({
          url: `/api/auth/drive/entity/${entity_id}/rename`,
          method: "PUT",
          data,
        }),
        invalidatesTags: (e1, e2, { parent_dir_id: parentDirectory }) => [
          {
            type: DriveApiTags.driveDirectoryContents,
            id: parentDirectory,
          },
        ],
      }),
      deleteContent: build.mutation<
        void,
        WithApiRequestMeta<{
          entity_id: string;
          id: string;
        }>
      >({
        query: ({ entity_id, id }) => ({
          url: `/api/auth/drive/entity/${entity_id}/${id}`,
          method: "DELETE",
        }),
        invalidatesTags: () => [DriveApiTags.driveDirectoryContents],
      }),
      moveFile: build.mutation<
        {
          entity_id: string;
          id: string;
          name: string;
          status: "invalid" | "valid";
          type: FileType;
          updated_at: string;
          workspace_id: string | null;
        },
        WithApiRequestMeta<{
          id: string;
          entityId: string;
          parent_id: string;
          name: string;
          workspace_id?: string;
        }>
      >({
        query: ({ entityId, meta: _meta, ...data }) => ({
          url: `/api/auth/drive/entity/${entityId}/move`,
          method: "PUT",
          data,
        }),
        invalidatesTags: [DriveApiTags.driveDirectoryContents],
      }),
    }),
  });
  return driveAPI;
};

export type DriveApi = ReturnType<typeof addDriveApi>;
